/* eslint-disable */
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import set from 'lodash/set';
import unset from 'lodash/unset';
import throwStorageCouldNotGetItemError from '../../Errors/Storage/StorageCouldNotGetItem.error';
import isString from '../../Support/Is/String/IsString.support';
import './Storage.service.i18n';

/**
 * StorageService
 * @class StorageService
 */
class StorageService {
  /**
   * The `key` of where to store the data inside the `store`
   * @type {string}
   * @memberof StorageService
   */
  _key;

  /**
   * The **cached** local representation of the store. This is done so that you don't need to keep reading from disk
   * @type {object}
   * @memberof StorageService
   */
  _store = {};

  /**
   * The promise that represents when the `store` is ready to be read
   * @type {Promise<void>}
   * @memberof StorageService
   */
  _storePromise;

  /**
   * The `StorageServiceAdapterContract` interface that will be the backing for persisting data
   * @type {StorageServiceAdapterContract}
   * @memberof StorageService
   */
  storageAdapter;

  /**
   * Creates an instance of `StorageService`
   * @param {StorageServiceCreateDto} dto The params required to create an instance of `StorageService`
   * @memberof StorageService
   */
  constructor({ key, storageAdapter }) {
    this.storageAdapter = storageAdapter;
    this._store = {};
    this._key = key;
    this._storePromise = this._createStorageStore();
  }

  /**
   * The number of items stored in the store
   * @readonly
   * @returns {number} The number of items
   * @memberof StorageService
   */
  get length() {
    return Object.keys(this._store).length;
  }

  /**
   * Get the store from the `storage` adapter
   * @param {any} [defaultValue={}] The data to return when there is no data in the store
   * @returns {Promise<void>} void
   * @memberof StorageService
   */
  __getStore = async (defaultValue = {}) => {
    let data;
    try {
      data = await Promise.resolve().then(() => this.storageAdapter.getItem(this._key));
    } catch (error) {
      return throwStorageCouldNotGetItemError(this._key, error);
    }
    if (data === null) {
      return defaultValue;
    }
    if (!isString(data)) {
      return data;
    }
    try {
      data = JSON.parse(data);
    } catch (error) {
      return data;
    }
    if (data === null) {
      return defaultValue;
    }
    return data;
  };

  /**
   * Set the store to the `storage` adapter
   * @param {any} store The data to store
   * @returns {Promise<void>} void
   * @memberof StorageService
   */
  __setStore = async (store) => {
    const json = JSON.stringify(store);
    await Promise.resolve().then(() => this.storageAdapter.setItem(this._key, json));
  };

  /**
   * Create the storage store. This method is also used to determine when the store is ready
   * @returns {Promise<any>} The data inside the store
   * @memberof StorageService
   */
  _createStorageStore = async () => {
    this._store = await this.__getStore();
    await this.__setStore(this._store);
  };

  /**
   * Clears all stored keys.
   * @returns {Promise<void>} void
   * @memberof StorageService
   */
  clear = async () => {
    await this._storePromise;
    this._store = {};
    await this.__setStore(this._store);
  };

  /**
   * Retrieve a value from the store by a `key`
   * @param {string} key The path of the property to get using `dot notation`
   * @param {any} [defaultValue=null] The default value to return in the event that the `given` key doen't retrieve a value
   * @returns {Promise<any>} The `value` from the store or the `defaultValue`
   * @memberof StorageService
   */
  getItem = async (key, defaultValue = null) => {
    await this._storePromise;
    const value = get(this._store, key, defaultValue);
    return value;
  };

  /**
   * The name of the _nth_ key in the `store`. The order of keys is user-agent defined, so you should not rely on it
   * @param {number} index An integer representing the number of the key you want to get the name of. This is a zero-based index
   * @returns {Promise<string>} The name of the key
   * @memberof StorageService
   */
  key = async (index) => {
    await this._storePromise;
    const names = Object.keys(this._store);
    const name = names[index];
    return name;
  };

  /**
   * Remove an item from the store by a `key`
   * @param {string} key The path of the `value` to remove
   * @returns {Promise<void>} void
   * @memberof StorageService
   */
  removeItem = async (key) => {
    await this._storePromise;
    unset(this._store, key);
    await this.__setStore(this._store);
  };

  /**
   * Store a given `value` by a `key`
   * @param {string} key The path of where to store the `value`
   * @param {any} value The `value` to store
   * @returns {Promise<void>} void
   * @memberof StorageService
   */
  setItem = async (key, value) => {
    await this._storePromise;
    const existingValue = get(this._store, key);
    if (isEqual(existingValue, value)) {
      return;
    }
    set(this._store, key, value);
    await this.__setStore(this._store);
  };
}

export default StorageService;
